/**
 * Base cause of a ticket travel's production cycle (A caminho, Em obra, Descarregando, Retornando, Na usina),
 * recorded once per travel by the travel analysis worker in reg_travel_analysis_cycles.
 *
 * Two outcomes are resolved only when the analysis is read, because they depend on the queried period:
 * a CYCLE_REGISTERED travel is complete only if every state reached the integration, and a
 * DISCHARGE_NOT_REGISTERED travel is split by the discharge sensor health of its truck.
 */
export enum TravelCycleCauses {
  CYCLE_REGISTERED = "cycle_registered",
  DISCHARGE_NOT_REGISTERED = "discharge_not_registered",
  TRACKER_WITHOUT_SIGNAL = "tracker_without_signal",
  INACTIVE_PLANT = "inactive_plant",
  JOBSITE_NOT_DETECTED = "jobsite_not_detected",
  DISCHARGE_OUTSIDE_JOBSITE = "discharge_outside_jobsite",
  JOBSITE_NEXT_TO_PLANT = "jobsite_next_to_plant",
  SHORT_PLANT_EXIT = "short_plant_exit",
  REPLACED_TICKET = "replaced_ticket",
  TWO_TICKETS = "two_tickets",
  OTHER = "other"
}

/**
 * Final outcome of a ticket travel shown by the "Análise de viagens" screen: the base cause with the
 * integration send and the discharge sensor health of the queried period resolved.
 */
export enum TravelCycleOutcomes {
  COMPLETE = "complete",
  SENSOR_STOPPED = "sensor_stopped",
  SENSOR_INTERMITTENT = "sensor_intermittent",
  DISCHARGE_ONE_OFF = "discharge_one_off",
  TRACKER_WITHOUT_SIGNAL = "tracker_without_signal",
  INACTIVE_PLANT = "inactive_plant",
  JOBSITE_NOT_DETECTED = "jobsite_not_detected",
  DISCHARGE_OUTSIDE_JOBSITE = "discharge_outside_jobsite",
  JOBSITE_NEXT_TO_PLANT = "jobsite_next_to_plant",
  SHORT_PLANT_EXIT = "short_plant_exit",
  REPLACED_TICKET = "replaced_ticket",
  TWO_TICKETS = "two_tickets",
  INTEGRATION_NOT_SENT = "integration_not_sent",
  OTHER = "other"
}

/** Where to act on a failed travel. */
export enum TravelCycleOutcomeGroups {
  EQUIPMENT = "equipment",
  REGISTRATION = "registration",
  ROUTINE = "routine",
  OTHERS = "others"
}

/** Group of each failure outcome (the complete cycle has none). */
export const TRAVEL_CYCLE_OUTCOME_GROUP: Record<Exclude<TravelCycleOutcomes, TravelCycleOutcomes.COMPLETE>, TravelCycleOutcomeGroups> = {
  [TravelCycleOutcomes.SENSOR_STOPPED]: TravelCycleOutcomeGroups.EQUIPMENT,
  [TravelCycleOutcomes.SENSOR_INTERMITTENT]: TravelCycleOutcomeGroups.EQUIPMENT,
  [TravelCycleOutcomes.DISCHARGE_ONE_OFF]: TravelCycleOutcomeGroups.EQUIPMENT,
  [TravelCycleOutcomes.TRACKER_WITHOUT_SIGNAL]: TravelCycleOutcomeGroups.EQUIPMENT,
  [TravelCycleOutcomes.INACTIVE_PLANT]: TravelCycleOutcomeGroups.REGISTRATION,
  [TravelCycleOutcomes.JOBSITE_NOT_DETECTED]: TravelCycleOutcomeGroups.REGISTRATION,
  [TravelCycleOutcomes.DISCHARGE_OUTSIDE_JOBSITE]: TravelCycleOutcomeGroups.REGISTRATION,
  [TravelCycleOutcomes.JOBSITE_NEXT_TO_PLANT]: TravelCycleOutcomeGroups.REGISTRATION,
  [TravelCycleOutcomes.SHORT_PLANT_EXIT]: TravelCycleOutcomeGroups.ROUTINE,
  [TravelCycleOutcomes.REPLACED_TICKET]: TravelCycleOutcomeGroups.ROUTINE,
  [TravelCycleOutcomes.TWO_TICKETS]: TravelCycleOutcomeGroups.ROUTINE,
  [TravelCycleOutcomes.INTEGRATION_NOT_SENT]: TravelCycleOutcomeGroups.OTHERS,
  [TravelCycleOutcomes.OTHER]: TravelCycleOutcomeGroups.OTHERS
};

/** Health of a truck's discharge sensor over the queried period. */
export enum DischargeSensorHealth {
  OK = "ok",
  STOPPED = "stopped",
  INTERMITTENT = "intermittent"
}

/** Thresholds of the discharge sensor health, applied to the trips that reached the jobsite in the period. */
export const DISCHARGE_SENSOR_HEALTH = {
  /** Below this share of discharges detected, the sensor is stopped. */
  STOPPED_BELOW_RATE: 0.1,
  /** Below this share (and at or above the stopped one), the sensor is intermittent. */
  INTERMITTENT_BELOW_RATE: 0.8,
  /** Fewer trips at the jobsite than this are too few to compute a rate. */
  MIN_TRIPS: 5,
  /** With too few trips, no discharge detected for this long still means a stopped sensor. */
  STALE_DAYS: 14
};

/** The ticket travels of the screen's period count against this goal of complete cycles, in percent. */
export const TRAVEL_CYCLE_TARGET_RATE = 90;
